import { Injectable, inject } from '@angular/core'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Update } from '@ngrx/entity'
import { GbMenuEditComponent } from 'libs/gb-dashboard/setup/feature/src/lib/gb-menu-edit/gb-menu-edit.component'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { catchError, exhaustMap, filter, finalize, forkJoin, map, of, switchMap, tap, withLatestFrom } from 'rxjs'
import { MenuItemsApiService } from './menu-items-api.service'
import * as MenuItemsActions from './menu-items.actions'
import { MenuItemsFacade } from './menu-items.facade'
import { MENU_ITEMS_LOADER, MenuItemsEntity } from './menu-items.models'
import { InfoboothsApiService } from 'libs/gb-dashboard/setup/data-access-infobooths/src/lib/+state/infobooths-api.service'
import * as InfoboothsActions from 'libs/gb-dashboard/setup/data-access-infobooths/src/lib/+state/infobooths.actions'
import { WidgetPagesApiService } from 'libs/gb-dashboard/branding/data-access/src/lib/+state/widget-pages-api.service'
import * as WidgetPagesActions from 'libs/gb-dashboard/branding/data-access/src/lib/+state/widget-pages.actions'
import { CategoriesApiService } from 'libs/gb-dashboard/item/data-access-categories/src/lib/+state/categories-api.service'
import * as CategoriesActions from 'libs/gb-dashboard/item/data-access-categories/src/lib/+state/categories.actions'
import { Store } from '@ngrx/store'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'

@Injectable()
export class MenuItemsEffects {
  private actions$ = inject(Actions)

  loadMenuItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuItemsActions.loadMenuItems),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) => {
        return this.menuItemsApiService.getMenuItems(urlName as string).pipe(
          map((response: MenuItemsEntity[]) => {
            return MenuItemsActions.loadMenuItemsSuccess({
              menuItems: response,
            })
          }),
          catchError((error) => {
            return of(MenuItemsActions.loadMenuItemsFailure({ error }))
          }),
        )
      }),
    ),
  )

  loadMenuItemResources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuItemsActions.loadMenuItemResources),
      tap(() => {
        this.gbLoaderService.showLoaderDialog(
          MENU_ITEMS_LOADER.loadResources.id,
          MENU_ITEMS_LOADER.loadResources.message,
        )
      }),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) => {
        return forkJoin({
          infobooths: this.infoboothsApiService.getInfobooths(urlName as string).pipe(catchError(() => of([]))),
          widgetPages: this.widgetPagesApiService.getWidgetPages(urlName as string).pipe(catchError(() => of([]))),
          oldWidgets: this.widgetPagesApiService.getOldWidgetPages(urlName as string).pipe(catchError(() => of([]))),
          categories: this.categoriesApiService.getCategories(urlName as string, undefined, undefined, '').pipe(
            map((response) => ({ results: response.results, count: response.count })),
            catchError(() => of({ results: [], count: 0 })),
          ),
        }).pipe(
          map((results) => {
            this.store.dispatch(InfoboothsActions.loadInfoboothsSuccess({ infobooths: results.infobooths }))
            this.store.dispatch(WidgetPagesActions.loadWidgetPagesSuccess({ widgetPages: results.widgetPages }))
            this.store.dispatch(WidgetPagesActions.loadOldWidgetsSuccess({ old_widgets: results.oldWidgets }))
            this.store.dispatch(
              CategoriesActions.setCategories({
                categories: results.categories.results,
                count: results.categories.count,
              }),
            )

            return MenuItemsActions.loadMenuItemResourcesSuccess()
          }),
          catchError((error) => {
            return of(MenuItemsActions.loadMenuItemResourcesFailure({ error }))
          }),
          finalize(() => {
            this.gbLoaderService.hideLoaderDialog(MENU_ITEMS_LOADER.loadResources.id)
          }),
        )
      }),
    ),
  )

  openEditMenuItemDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuItemsActions.selectMenuItem),
        exhaustMap(() =>
          this.gbDashboardDialogService.getDialogObs(GbMenuEditComponent, {
            disableClose: true,
            width: '600px',
            mobileFullScreen: true,
          }),
        ),
      ),
    { dispatch: false },
  )

  openCreateMenuItemDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuItemsActions.openCreateMenuItemDialog),
        exhaustMap(() =>
          this.gbDashboardDialogService.getDialogObs(GbMenuEditComponent, {
            disableClose: true,
            width: '600px',
            mobileFullScreen: true,
          }),
        ),
      ),
    { dispatch: false },
  )

  createMenuItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuItemsActions.createMenuItem),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        const payload: MenuItemsEntity = {
          ...action.menuItem,
          auction: urlName,
        } as MenuItemsEntity
        return this.menuItemsApiService.createMenuItem(payload).pipe(
          map((response: MenuItemsEntity) => {
            return MenuItemsActions.createMenuItemSuccess({
              menuItem: response,
              doNotShowSuccessToast: action.doNotShowSuccessToast,
            })
          }),
          catchError((error) => {
            return of(MenuItemsActions.createMenuItemFailure({ error }))
          }),
        )
      }),
    ),
  )

  closeCreateMenuItemDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuItemsActions.updateMenuItemSuccess, MenuItemsActions.createMenuItemSuccess),
        tap(() => this.gbDashboardDialogService.closeDialog(GbMenuEditComponent, true)),
      ),
    { dispatch: false },
  )

  updateMenuItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuItemsActions.updateMenuItem),
      withLatestFrom(this.menuItemsFacade.selectedMenuItem$),
      switchMap(([action, selectedMenuItem]) => {
        const payload: MenuItemsEntity = {
          ...selectedMenuItem,
          ...action.menuItem,
        } as MenuItemsEntity
        return this.menuItemsApiService.updateMenuItem(payload).pipe(
          map((response: MenuItemsEntity) => {
            return MenuItemsActions.updateMenuItemSuccess({
              update: {
                id: response.uuid,
                changes: response,
              },
            })
          }),
          catchError((error) => {
            return of(MenuItemsActions.updateMenuItemFailure({ error }))
          }),
        )
      }),
    ),
  )

  updateMenuItemsOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuItemsActions.updateMenuItemOrder),
      withLatestFrom(this.menuItemsFacade.allMenuItems$, this.selectedAuctionFacade.urlName$),
      map(([action, allMenuItems, urlName]) => ({
        action,
        allMenuItems,
        menuItemsToUpdate: this.buildMenuItemsToUpdate(action.menuItemUuids, allMenuItems),
        urlName,
      })),
      filter(({ menuItemsToUpdate }) => menuItemsToUpdate.length > 0),
      tap(({ action }) => {
        if (!action.doNotShowLoader)
          this.gbLoaderService.showLoaderDialog(
            MENU_ITEMS_LOADER.reorderMenuItems.id,
            MENU_ITEMS_LOADER.reorderMenuItems.message,
          )
      }),
      switchMap(({ menuItemsToUpdate, urlName }) => {
        return this.menuItemsApiService
          .reorderMenuItems(
            urlName,
            menuItemsToUpdate.map((item) => ({ uuid: item.uuid, order: item.order })),
          )
          .pipe(
            map(() =>
              MenuItemsActions.updateMenuItemOrderSuccess({
                menuItems: menuItemsToUpdate.map((item) => ({ id: item.uuid, changes: item })),
              }),
            ),
            catchError((error) => {
              this.gbLoaderService.hideLoaderDialog(MENU_ITEMS_LOADER.reorderMenuItems.id)
              return of(MenuItemsActions.updateMenuItemOrderFailure({ error }))
            }),
          )
      }),
    ),
  )

  private buildMenuItemsToUpdate(newUuids: string[], currentMenuItems: MenuItemsEntity[]): MenuItemsEntity[] {
    const updatedMenuItems: MenuItemsEntity[] = []

    newUuids.forEach((newUuid, newIndex) => {
      const currentItem = currentMenuItems.find((item) => item.uuid === newUuid)
      if (currentItem && currentItem.order !== newIndex) {
        const updatedItem = { ...currentItem, order: newIndex }
        updatedMenuItems.push(updatedItem)
      }
    })

    return updatedMenuItems
  }

  deleteMenuItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuItemsActions.deleteMenuItem),
      exhaustMap((action) =>
        action.shouldShowConfirmDialog === false
          ? of(action)
          : this.gbDashboardDialogService.filterByConfirmDialog(
              {
                title: 'SETTINGS.MENU.CONFIRM_DELETE_TITLE',
                body: 'SETTINGS.MENU.CONFIRM_DELETE_BODY',
                confirmButtonText: 'SETTINGS.MENU.CONFIRM_DELETE_CONFIRM',
                cancelButtonText: 'SETTINGS.MENU.CONFIRM_DELETE_CANCEL',
              },
              action,
            ),
      ),
      tap(() => {
        this.gbLoaderService.showLoaderDialog(
          MENU_ITEMS_LOADER.deleteMenuItem.id,
          MENU_ITEMS_LOADER.deleteMenuItem.message,
        )
      }),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.menuItemsApiService.deleteMenuItem(urlName, action.uuid).pipe(
          map(() =>
            MenuItemsActions.deleteMenuItemSuccess({
              uuid: action.uuid,
            }),
          ),
          catchError((error) => of(MenuItemsActions.deleteMenuItemFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(MENU_ITEMS_LOADER.deleteMenuItem.id)),
        ),
      ),
    ),
  )

  showCreateSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuItemsActions.createMenuItemSuccess),
        tap((action) => {
          if (action.doNotShowSuccessToast) return
          this.gbNotificationService.success('SETTINGS.MENU.MENU_ITEM_CREATE_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  showUpdateSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuItemsActions.updateMenuItemSuccess),
        tap(() => {
          this.gbNotificationService.success('SETTINGS.MENU.MENU_ITEM_UPDATE_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  showDeleteSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuItemsActions.deleteMenuItemSuccess),
        tap(() => {
          this.gbNotificationService.success('SETTINGS.MENU.MENU_ITEM_DELETE_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  showReorderSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MenuItemsActions.updateMenuItemOrderSuccess),
        tap(() => {
          this.gbLoaderService.hideLoaderDialog(MENU_ITEMS_LOADER.reorderMenuItems.id)
          this.gbNotificationService.success('SETTINGS.MENU.REORDER_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  putWidgetPageMenuItemOnTop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MenuItemsActions.createMenuItemSuccess),
      filter((action) => action.menuItem.button_type === 'widget_page'),
      withLatestFrom(this.menuItemsFacade.allMenuItems$),
      map(([action, allMenuItems]) => {
        const newMenuItem = action.menuItem
        const otherMenuItems = allMenuItems.filter((item) => item.uuid !== newMenuItem.uuid)

        // Create a new order with the new menu item at the top
        const new_uuids = [
          newMenuItem.uuid,
          ...otherMenuItems.sort((a, b) => a.order - b.order).map((item) => item.uuid),
        ]

        return MenuItemsActions.updateMenuItemOrder({ menuItemUuids: new_uuids, doNotShowLoader: true })
      }),
    ),
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          MenuItemsActions.createMenuItemFailure,
          MenuItemsActions.updateMenuItemFailure,
          MenuItemsActions.deleteMenuItemFailure,
        ),
        tap((action) => {
          const error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          if (error && typeof error == 'string') {
            this.gbNotificationService.error(error)
          } else {
            this.gbNotificationService.error('GENERIC.ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private selectedAuctionFacade: SelectedAuctionFacade,
    private menuItemsApiService: MenuItemsApiService,
    private menuItemsFacade: MenuItemsFacade,
    private gbNotificationService: GbNotificationService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbLoaderService: GbLoaderService,
    private infoboothsApiService: InfoboothsApiService,
    private widgetPagesApiService: WidgetPagesApiService,
    private categoriesApiService: CategoriesApiService,
    private store: Store,
  ) {}
}
